/**
 * @typedef {Object} WarnFilter
 * @property {string[]} messageIncludes - substrings that must appear in the warning message
 * @property {string[]} [componentIncludes] - optional substrings to match component stack
 */

/**
 * Usage example:
 *
 * In this plugin, you can selectively ignore Vue warnings by specifying substrings
 * to match in the warning message and optionally in the component stack.
 *
 * Example configuration:
 *
 * const ignoredWarnings: WarnFilter[] = [
 *   {
 *     // Ignore any warning message that contains this substring
 *     messageIncludes: [
 *       'Property "value" was accessed during render but is not defined on instance',
 *     ],
 *     // Only ignore if these components also appear in the stack trace
 *     componentIncludes: ['<LocaleReceiver', '<NuxtRoot>']
 *   },
 *   {
 *     // Ignore any warning that contains this substring in the message, regardless of component
 *     messageIncludes: ['Another warning substring to ignore'],
 *   }
 * ]
 *
 * Behavior:
 * - If a warning matches all messageIncludes and all componentIncludes (if specified), it is suppressed.
 * - All other warnings are displayed exactly as Vue would normally show them.
 */

const originalWarn = console.warn

// Configure ignored warnings here
const ignoredWarnings = [
  {
    messageIncludes: [
      '#page_canvas is undefined',
    ]
  },
]

console.warn = (...args) => {
  if (!args.length || typeof args[0] !== 'string') {
    originalWarn(...args)
    return
  }

  // Flatten all arguments into a single string for pattern matching
  const fullMessage = args
    .map(a => (typeof a === 'string' ? a : JSON.stringify(a)))
    .join('\n')

  // Check if any ignored warning pattern matches
  const shouldIgnore = ignoredWarnings.some(pattern => {
    const messageMatches = pattern.messageIncludes.every(sub => fullMessage.includes(sub))
    const componentMatches = pattern.componentIncludes
      ? pattern.componentIncludes.every(sub => fullMessage.includes(sub))
      : true

    return messageMatches && componentMatches
  })

  if (shouldIgnore) return

  // Otherwise, output exactly as Vue would
  originalWarn(...args)
}

export default {}